/**
 * Timeline pure helpers.
 */
import {
  hasAuthoritativeWorkflowState,
  workflowRunState,
} from './workflowRunProjectionState.js';

export function mergeTimelineItems(coreTimeline, generativeBlocks) {
  return {
    isEmpty: coreTimeline.isEmpty && generativeBlocks.length === 0,
    items: [...coreTimeline.items, ...generativeBlocks],
  };
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse) {
  return condition ? whenTrue : whenFalse;
}

function resolveExplicitRoundKey(item) {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const intakeSessionId = String(firstTruthy(item.intake_session_id, item.intakeSessionId) ?? '').trim();
  const direct = String(firstTruthy(item.roundKey, item.round_key) ?? '').trim();
  const payload = Object.assign({}, item.payload);
  const payloadIntakeSession = String(firstTruthy(payload.intake_session_id, payload.intakeSessionId) ?? '').trim();
  const messageRoundKey = conditionalValue(
    item.type === 'message',
    firstTruthy(item.client_request_id, item.clientRequestId),
    ''
  );
  const payloadRoundKey = conditionalValue(
    item.type !== 'message',
    firstTruthy(
      conditionalValue(Boolean(payloadIntakeSession), `intake:${payloadIntakeSession}`, ''),
      payload.round_key,
      payload.roundKey,
      payload.client_request_id,
      payload.clientRequestId
    ),
    ''
  );
  return String(firstTruthy(
    conditionalValue(Boolean(intakeSessionId), `intake:${intakeSessionId}`, ''),
    direct,
    messageRoundKey,
    payloadRoundKey
  ) ?? '').trim();
}

function classifyRoundItem(item) {
  if (!item || typeof item !== 'object') {
    return 'artifact';
  }
  const classifications = new Map([
    ['message:user', 'user_message'],
    ['message', 'assistant_output'],
    ['streaming', 'assistant_output'],
    ['thinking', 'assistant_process'],
    ['orchestration_status', 'assistant_process'],
  ]);
  return firstTruthy(classifications.get(`${item.type}:${item.role}`), classifications.get(item.type), 'round_artifact');
}

function roundItems(round) {
  const record = Object.assign({}, round);
  return [
    ...(Array.isArray(record.assistantProcessItems) ? record.assistantProcessItems : []),
    ...(Array.isArray(record.assistantOutputItems) ? record.assistantOutputItems : []),
    ...(Array.isArray(record.artifactItems) ? record.artifactItems : []),
  ];
}

function shouldKeepStandaloneRound(round) {
  const items = roundItems(round);
  const hasAuthoritativeFailedWorkflow = items.some((item) => (
    hasAuthoritativeWorkflowState(item) && workflowRunState(item) === 'failed'
  ));
  return [
    Boolean(Object.assign({}, round).explicitRoundKey),
    hasAuthoritativeFailedWorkflow || items.some((item) => {
      const record = Object.assign({}, item);
      return [record.loading, record.pending].some((value) => value === true);
    }),
  ].every(Boolean);
}

export function buildRoundEntries(items = []) {
  const normalizedItems = Array.isArray(items) ? items : [];
  const rounds = [];
  const roundMap = new Map();
  let activeRoundKey = '';
  let fallbackCounter = 0;

  const ensureRound = (roundKey, explicitRoundKey) => {
    const normalizedRoundKey = String(roundKey ?? '').trim();
    fallbackCounter += Number(!normalizedRoundKey);
    const key = conditionalValue(Boolean(normalizedRoundKey), normalizedRoundKey, `timeline_round_${fallbackCounter}`);
    if (roundMap.has(key)) {
      const existing = roundMap.get(key);
      existing.explicitRoundKey = [existing.explicitRoundKey, explicitRoundKey].some(Boolean);
      return existing;
    }
    const nextRound = {
      key,
      explicitRoundKey,
      userMessage: null,
      assistantItems: [],
      assistantProcessItems: [],
      assistantOutputItems: [],
      artifactItems: [],
    };
    roundMap.set(key, nextRound);
    rounds.push(nextRound);
    return nextRound;
  };

  normalizedItems.forEach((item, index) => {
    if (!item || typeof item !== 'object') {
      return;
    }
    const bucket = classifyRoundItem(item);
    const explicitRoundKey = resolveExplicitRoundKey(item);
    const fallbackUserRoundKey = conditionalValue(
      bucket === 'user_message',
      `user_anchor_${String(firstTruthy(item.id, `idx_${index}`))}`,
      ''
    );
    const roundKey = firstTruthy(explicitRoundKey, fallbackUserRoundKey, activeRoundKey) ?? '';
    const round = ensureRound(roundKey, Boolean(explicitRoundKey));
    const itemWithRound = conditionalValue(Boolean(explicitRoundKey), item, { ...item, roundKey: round.key });
    const bucketActions = new Map([
      ['user_message', () => {
        activeRoundKey = round.key;
        round.userMessage = itemWithRound;
      }],
      ['assistant_process', () => {
        round.assistantProcessItems.push(itemWithRound);
        round.assistantItems.push(itemWithRound);
      }],
      ['assistant_output', () => {
        round.assistantOutputItems.push(itemWithRound);
        round.assistantItems.push(itemWithRound);
      }],
      ['round_artifact', () => round.artifactItems.push(itemWithRound)],
    ]);
    bucketActions.get(bucket)();
  });

  const rekeyItems = (sourceItems, roundKey) => (
    (Array.isArray(sourceItems) ? sourceItems : []).map((item) => (
      item && typeof item === 'object'
        ? { ...item, roundKey }
        : item
    ))
  );

  const appendRoundItems = (target, source) => {
    target.assistantItems.push(...rekeyItems(source.assistantItems, target.key));
    target.assistantProcessItems.push(...rekeyItems(source.assistantProcessItems, target.key));
    target.assistantOutputItems.push(...rekeyItems(source.assistantOutputItems, target.key));
    target.artifactItems.push(...rekeyItems(source.artifactItems, target.key));
  };

  const prependRoundItems = (target, source) => {
    target.assistantItems.unshift(...rekeyItems(source.assistantItems, target.key));
    target.assistantProcessItems.unshift(...rekeyItems(source.assistantProcessItems, target.key));
    target.assistantOutputItems.unshift(...rekeyItems(source.assistantOutputItems, target.key));
    target.artifactItems.unshift(...rekeyItems(source.artifactItems, target.key));
  };

  const shouldBindLeadingRoundToNextUser = (round) => (
    [
      (Array.isArray(round?.assistantProcessItems) ? round.assistantProcessItems : []).length > 0,
      (Array.isArray(round?.assistantOutputItems) ? round.assistantOutputItems : []).length === 0,
      (Array.isArray(round?.artifactItems) ? round.artifactItems : []).length === 0,
    ].every(Boolean)
  );

  const mergedRounds = [];
  let pendingLeadingRound = null;
  rounds.forEach((round) => {
    const hasUserAnchor = Boolean(round?.userMessage);
    if (!hasUserAnchor && mergedRounds.length === 0) {
      if (pendingLeadingRound) {
        appendRoundItems(pendingLeadingRound, round);
        return;
      }
      pendingLeadingRound = round;
      return;
    }
    if (hasUserAnchor) {
      if (pendingLeadingRound) {
        const mergePendingRound = conditionalValue(
          shouldBindLeadingRoundToNextUser(pendingLeadingRound),
          () => prependRoundItems(round, pendingLeadingRound),
          () => mergedRounds.push(pendingLeadingRound)
        );
        mergePendingRound();
        pendingLeadingRound = null;
      }
      mergedRounds.push(round);
      return;
    }
    const previous = mergedRounds[mergedRounds.length - 1];
    if (shouldKeepStandaloneRound(round)) {
      mergedRounds.push(round);
      return;
    }
    appendRoundItems(previous, round);
  });

  if (pendingLeadingRound) {
    mergedRounds.push(pendingLeadingRound);
  }

  return mergedRounds;
}
